import { ElMessage } from 'element-plus'
import { ref, unref, watch } from 'vue'
import {
  cancelSassmallProductFavorite,
  checkSassmallProductFavorite,
  favoriteSassmallProduct,
  recordSassmallProductFootprint,
} from '@/sassmall/api/activity'
import { isSassmallResponseSuccess } from '@/sassmall/api/client'
import { getSassmallToken } from '@/sassmall/storage'


export function useCollect(itemId, options = {}) {
  const hasCollect = ref(false)
  const getItemId = () => unref(itemId)
  const platform = normalizePlatform(options?.platformType)
  const recordedFootprints = new Set()

  function normalizePlatform(value) {
    const normalized = String(value || '').trim().toUpperCase()
    if (['TB', 'TBGLOBAL', 'TAOBAO', 'TMALL'].includes(normalized)) return 'TAOBAO'
    if (['1688', 'ALI1688', 'ALI_1688', 'ALIBABA'].includes(normalized)) return 'ALI_1688'
    if (['WD', 'WEIDIAN'].includes(normalized)) return 'WEIDIAN'
    return normalized || 'UNKNOWN'
  }

  function getSnapshot() {
    const currentItemId = String(getItemId() ?? '').trim()
    const titleRef = unref(options?.itemTitle)
    const picRef = unref(options?.itemPic)
    const shopNameRef = unref(options?.itemShopName)
    const shopIdRef = unref(options?.itemShopId)
    const priceRef = unref(options?.itemPrice)
    const priceNum = Number(priceRef)
    const sourcePrice = Number.isFinite(priceNum)
      ? Number((options?.priceInCents === false ? priceNum : priceNum / 100).toFixed(2))
      : 0
    const rawUrl = String(unref(options?.rawUrl) || (process.client ? window.location.href : '')).trim()
    return {
      platform,
      itemId: currentItemId,
      title: String(titleRef || '').trim(),
      imageUrl: String(picRef || '').trim(),
      shopName: String(shopNameRef || '').trim() || undefined,
      shopId: shopIdRef == null ? undefined : String(shopIdRef).trim() || undefined,
      sourcePrice,
      sourceCurrency: String(unref(options?.sourceCurrency) || 'CNY').trim(),
      rawUrl: rawUrl || undefined,
      categoryId: unref(options?.categoryId) ?? undefined,
      categoryName: String(unref(options?.categoryName) || '').trim() || undefined,
    }
  }

  async function addToCollect() {
    if (!getSassmallToken()) {
      ElMessage.warning('请先登录')
      return
    }
    const snapshot = getSnapshot()
    if (!snapshot.itemId || !snapshot.title || !snapshot.imageUrl) return
    const res = await favoriteSassmallProduct(snapshot)
    if (isSassmallResponseSuccess(res)) {
      ElMessage({
        message:$t('messages.collectSuccess') ,
        type: 'success',
        plain: true,
      })
      hasCollect.value = true
    }
  }

  async function cancelCollect() {
    if (!getSassmallToken()) {
      ElMessage.warning('请先登录')
      return
    }
    const currentItemId = String(getItemId() ?? '').trim()
    if (!currentItemId) return
    const res = await cancelSassmallProductFavorite(platform, currentItemId)
    if (isSassmallResponseSuccess(res)) {
      ElMessage({
        message:$t('messages.cancelCollectSuccess') ,
        type: 'success',
        plain: true,
      })
      hasCollect.value = false
    }
  }

  async function queryHasCollect() {
    if (!process.client) return
    if (!getSassmallToken()) {
      hasCollect.value = false
      return
    }
    if (!String(getItemId() || '').trim()) {
      hasCollect.value = false
      return
    }
    const res = await checkSassmallProductFavorite(platform, String(getItemId()).trim())
    hasCollect.value = isSassmallResponseSuccess(res) && Boolean(res.data)
  }

  async function recordFootprint() {
    if (!process.client || !getSassmallToken()) return
    const snapshot = getSnapshot()
    if (!snapshot.itemId || !snapshot.title || !snapshot.imageUrl) return
    const reportKey = `${snapshot.platform}:${snapshot.itemId}`
    if (recordedFootprints.has(reportKey)) return
    recordedFootprints.add(reportKey)
    try {
      await recordSassmallProductFootprint(snapshot)
    }
    catch (error) {
      recordedFootprints.delete(reportKey)
      console.error('记录 Sassmall 商品足迹失败', error)
    }
  }

  if (process.client) {
    watch(
      () => [String(getItemId() || '').trim(), getSassmallToken()],
      () => {
        queryHasCollect()
      },
      { immediate: true },
    )
  }

  return {
    hasCollect,
    addToCollect,
    cancelCollect,
    queryHasCollect,
    recordFootprint,
  }
}
